import { OverlayContainer } from '@angular/cdk/overlay';
import {
    AfterViewInit,
    Component,
    ElementRef,
    Input,
    NgZone,
    OnDestroy,
    ViewChild
} from '@angular/core';
import { MatDialog } from '@angular/material';
import highchartsMore from 'highcharts/highcharts-more';
import * as Highstock from 'highcharts/highstock';
import { Options } from 'highcharts/highstock';
import highchartsAnnotations from 'highcharts/modules/annotations';
import highchartsExporting from 'highcharts/modules/exporting';
import offlineExporting from 'highcharts/modules/offline-exporting';
import { cloneDeep } from 'lodash';
import { Subscription } from 'rxjs';

import valueInLegend from '../../addOns/value-in-legend';
import { Annotation } from '../../models/annotation';
import { COLOR_THEMES, getColorForEventType } from '../../models/color-themes';
import {
    DatasetType,
    generateDataWithGaps,
    getLimitZones,
    getUsedDiscreteVals,
    getYNameAndUnits,
    IDataset
} from '../../models/dataset';
import { IEvent } from '../../models/events-data';
import { setColorTheme, setEventsOverlay, setRangeChangeHandler, setSeriesClickHandler } from '../../models/highstock-config';
import { newAxisConfig } from '../../models/highstock-config';
import { legendFormatter, setXAxisLabels } from '../../models/highstock-formatters';
import { DEFAULT_MENU_OPTIONS, IMenuOptions, processMenuConfig } from '../../models/menu-options';
import { MouseMode } from '../../models/mouse-mode';
import { DEFAULT_UI_OPTIONS, IUiOptions } from '../../models/ui-options';
import { AnnotationsService } from '../../services/annotations.service';
import { HighchartsConfigService } from '../../services/config.service';
import { DatasetService } from '../../services/dataset.service';
import { EventsService } from '../../services/events.service';
import { MenuOptionsService } from '../../services/menu-options.service';
import { MouseModeService } from '../../services/mouse-state.service';
import { PlotsService } from '../../services/plots.service';
import { UiOptionsService } from '../../services/ui-options.service';

import { onMouseDown, onMouseMove, onMouseUp } from './mouse-handlers';

// add custom modules to highstock
highchartsExporting( Highstock );
highchartsMore( Highstock );
offlineExporting( Highstock );
highchartsAnnotations( Highstock );
valueInLegend( Highstock );

/** Base container for a line analog or discrete plot. */
@Component({
    selector: 'sci-line-chart',
    templateUrl: './line-chart.component.html',
    styleUrls: [ './line-chart.component.scss' ]
})
export class LineChartComponent implements AfterViewInit, OnDestroy {
    @ViewChild('plotContainer', { static: false }) plotContainer: ElementRef;
    @Input() plotId: string;

    chart: Highstock.Chart;
    annotationState = {
        dragStart: null,
        dragEnd: null,
        currentAnnotation: null
    };
    currentMouseMode: MouseMode;
    eventListeners: EventListener[] = [];
    config: Options;
    menuOptions: IMenuOptions = DEFAULT_MENU_OPTIONS;
    subscriptions: Subscription[] = [];
    yAxisBreaks: any[];
    allowGaps: boolean;
    gapThreshold: number;
    showSingleLegendXValue: boolean;
    uiOptions: IUiOptions = DEFAULT_UI_OPTIONS;
    rendering = true;
    events;
    eventTypes;

    /** controls if we have displayed gaps in the data and when to show them */
    setGapOptions = ( () => {
        // we can use function closure to save these values between function calls
        let oldAllowGaps: boolean;
        let oldGapThreshold: number;

        return () => {
            // Changing the gaps is resource intensive, so I want to protect it from being called more times than is needed.
            if ( oldAllowGaps !== this.allowGaps || oldGapThreshold !== this.gapThreshold ) {
                oldAllowGaps = this.allowGaps;
                oldGapThreshold = this.gapThreshold;
                this.addDatasetsToChart( this._datasetService.getPlotDatasets( this.plotId ).datasets );
            }
        };
    })();

    constructor(
        public plotsService: PlotsService,
        public mouseModeService: MouseModeService,
        public annotationsService: AnnotationsService,
        public matDialog: MatDialog,
        public zone: NgZone,
        private _configService: HighchartsConfigService,
        private _datasetService: DatasetService,
        private _menuOptionsService: MenuOptionsService,
        private _uiOptionsService: UiOptionsService,
        private _overlayContainer: OverlayContainer,
        private _eventsService: EventsService
    ) {}

    ngAfterViewInit() {
        // register mouse handlers
        this.plotContainer.nativeElement.addEventListener('mousedown',  event => onMouseDown.call( this, event ) );
        this.plotContainer.nativeElement.addEventListener('mouseup',  event => onMouseUp.call( this, event ) );
        this.plotContainer.nativeElement.addEventListener('mousemove',  event => onMouseMove.call( this, event ) );

        setTimeout( () => {
            // config service
            this.subscriptions.push( this._configService.getPlotConfig$( this.plotId ).subscribe( config => {
                this.config = config;
                if ( this.chart == null ) {
                    this.createChart();
                } else {
                    this.chart.update( cloneDeep( this.config ) );
                }
            }));

            this.subscriptions.push( this.mouseModeService.getMouseMode$().subscribe( newMode => {
                let newZoomType;
                if ( newMode === MouseMode.ZOOM_X ) {
                    newZoomType = 'x';
                } else if ( newMode === MouseMode.ZOOM_XY ) {
                    newZoomType = 'xy';
                }
                this.config.chart.zoomType = newZoomType;
                this.chart.update({ chart: { zoomType: newZoomType } }, false );
                this.currentMouseMode = newMode;
            }));

            this.subscriptions.push( this.plotsService.getXPosition$().subscribe( update => {
                if ( !update || update.id === this.plotId ) {
                    return;
                }
                // we use a fakeEvt to simulate events on other plots
                const fakeEvt = { chartX: null, chartY: null, clientX: null, clientY: null };

                if ( this.chart ) {
                    fakeEvt.chartX = this.chart.xAxis[0].toPixels( update.position, false );
                    fakeEvt.clientX = fakeEvt.chartX;
                    // this simulates a mouseover on our other plots
                    this.chart.pointer['runPointActions']( fakeEvt );
                }
            }));

            this.subscriptions.push( this.plotsService.getPlotReflow$().subscribe( () => {
                if ( this.chart ) { this.chart.reflow(); }
            }));

            this.subscriptions.push( this.plotsService.getDownloadChartImage$().subscribe( imageConfig => {
                if ( imageConfig.plotId === this.plotId ) {
                    try {
                        this.chart.exportChartLocal({
                            filename: imageConfig.filename,
                            type: imageConfig.mime,
                            sourceWidth: this.chart.container.clientWidth
                        }, undefined );
                    } catch ( e ) {
                        alert( 'Sorry, your browser does not support this feature. Try using Chrome or Firefox.' );
                    }
                }
            }));

            this.subscriptions.push( this.plotsService.getZoom$().subscribe( newZoom => {
                if ( newZoom != null && newZoom.id !== this.plotId ) {
                    this.chart.xAxis[0].setExtremes( newZoom.start, newZoom.end );
                }
            }));

            this.subscriptions.push( this.annotationsService.getAnnotations$( this.plotId ).subscribe( annotations => {
                this.zone.runOutsideAngular( () => {
                    // TODO: find more efficient way to add/remove annotations
                    // find annotations to remove
                    this.chart['annotations'].forEach( chartAnnotation => {
                        if ( !annotations.find( ( annotation: Annotation ) => chartAnnotation.options.id === annotation.id ) ) {
                            this.chart.removeAnnotation( chartAnnotation.options.id );
                        }
                    });

                    // find annotations to add
                    annotations.forEach( ( annotation: Annotation ) => {
                        if ( !this.chart['annotations'].find( chartAnnotation => chartAnnotation.options.id === annotation.id ) ) {
                            this.chart.addAnnotation( annotation.getHighstockOptions(), false );
                        }
                    });

                    this.chart['drawAnnotations']();
                });
            }));

            this.subscriptions.push( this.plotsService.getYAxisLabelWidth$().subscribe( newWidth => {
                if ( newWidth == null ) {
                    newWidth = undefined;
                } else {
                    newWidth += 14;
                    // prevent us from setting the yaxis too skinny
                    if ( newWidth < 30 ) {
                        newWidth += 10;
                    }
                }
                if ( this.chart != null ) {
                    // 14px represents the padding on the left side of the yAxis
                    this.chart.update({ chart: { marginLeft: newWidth } }, false);
                }
            }));

            this.subscriptions.push( this._menuOptionsService.getMenuOptions$( this.plotId ).subscribe( newOptions => {
                this.menuOptions = newOptions;
                this.updateMenuOptions( newOptions );
            }));

            this.subscriptions.push( this._uiOptionsService.getUiOptions$().subscribe( newOptions => {
                if ( this.uiOptions.darkTheme !== newOptions.darkTheme ) {
                    this.uiOptions = cloneDeep( newOptions );
                    this.handleColorThemeChange();
                    this.chart.update( cloneDeep( this.config ));
                } else {
                    this.uiOptions = cloneDeep( newOptions );
                }
            }));
        }, 0 );
    }

    /** teardown our listeners and subscriptions to prevent memory leaks */
    ngOnDestroy() {
        this.plotContainer.nativeElement.removeEventListener('mousedown',  event => onMouseDown.call( this, event ) );
        this.plotContainer.nativeElement.removeEventListener('mouseup',  event => onMouseUp.call( this, event ) );
        this.plotContainer.nativeElement.removeEventListener('mousemove',  event => onMouseMove.call( this, event ) );
        this.subscriptions.forEach( subscription => subscription.unsubscribe() );
    }

    /** destroys our old plot and generates a new one, sometimes necessary depending on what config we are updating */
    createChart() {
        let extremes;
        this.config = this._configService.getPlotConfig( this.plotId );
        this.config.chart.events = {
            load: () => {
                this.rendering = false;
            }
        };
        if ( this.chart != null ) {
            // save our current range settings
            extremes = this.chart.xAxis[0].getExtremes();
        }
        // get the datasets we have loaded, we don't check if loading because this component isn't active until all datasets are loaded
        const datasets = this._datasetService.getPlotDatasets( this.plotId ).datasets;

        datasets.forEach( ( dataset, index ) => {
            // add an axis for each dataset past the first one
            // y axis 1 is taken by the events axis
            if ( index > 0 ) { this.config.yAxis[ index + 1 ] = newAxisConfig; }
        });

        // if we have offset datasets, we want to show x values per dataset
        this.showSingleLegendXValue = datasets.every( dataset => dataset.offset === 0 );

        // register a handler for when the x axis range changes
        this.config = setRangeChangeHandler( this.config, event => {
            // when event trigger is null or undefined, we are setting the extremes in the code and don't want to update
            if ( event.trigger != null ) { this.plotsService.setZoom( event.min, event.max, this.plotId ); }
        });
        // register click handlers
        this.config = setSeriesClickHandler( this.config );

        // register legend label formatter
        this.config.legend.labelFormatter = legendFormatter( datasets, this.showSingleLegendXValue );

        // overwrite our chart with a brand new chart
        this.chart = Highstock.stockChart( this.plotContainer.nativeElement, cloneDeep( this.config ) );

        // listen to the width of the yaxis labels
        this.config.chart.events = { redraw:
            ( event: any ) => {
                if ( event.target.yAxis != null ) {
                    this.plotsService.setYAxisLabelWidth( this.plotId, event.target.yAxis[0].maxLabelLength );
                }
            }
        };

        this.updateMenuOptions( this.menuOptions );

        // add our datasets to the chart
        this.addDatasetsToChart( datasets );

        // get spacecraft events data
        if ( datasets[0].eventsUrl != null ) {
            const eventsObject = this._eventsService.getPlotEvents( this.plotId );
            this.events = eventsObject.events;
            this.eventTypes = eventsObject.types;
            this.addEventsToChart();
        }

        if ( extremes != null ) {
            // preserve current zoom
            this.chart.xAxis[0].min = extremes.min;
            this.chart.xAxis[0].max = extremes.max;
            this.chart.xAxis[0]['userMax'] = extremes.userMax;
            this.chart.xAxis[0]['userMin'] = extremes.userMin;
        }
    }

    /** remove all series from the current chart */
    removeAllSeriesFromChart() {
        while ( this.chart.series.length > 0 ) {
            this.chart.series[0].remove( true );
        }
    }

    addDatasetsToChart( datasets: IDataset[] ) {
        this.removeAllSeriesFromChart();
        datasets.forEach( ( ds, index ) => {
            ds.color = COLOR_THEMES.light.colors[ index % COLOR_THEMES.light.colors.length ];
            this.addDatasetToChart( ds );
        });
        this.chart.redraw();
    }

    /** add a dataset to the chart object. Note, this does not redraw the chart */
    addDatasetToChart( dataset: IDataset ) {
        const data = this.allowGaps ? generateDataWithGaps( dataset.data, dataset.indexes, this.gapThreshold )
                                    : dataset.data;
        this.zone.runOutsideAngular( () => {
            // add the line series
            this.chart.addSeries({
                type: 'line',
                name: getYNameAndUnits( dataset ),
                data: data,
                color: dataset.color,
                id: dataset.uid
            }, false );
            // add the min/max range series
            if ( dataset.type === DatasetType.analog && dataset.minMaxData ) {
                const minMaxData = this.allowGaps ? generateDataWithGaps( dataset.minMaxData, dataset.indexes, this.gapThreshold )
                                                  : dataset.minMaxData;
                this.chart.addSeries({
                    type: 'arearange',
                    name: 'range',
                    data: minMaxData,
                    color: dataset.color,
                    id: dataset.uid
                }, false );
            }
        });
    }

    /** Set the visible range back to the full range */
    resetZoom = () => {
        this.chart.xAxis[0].setExtremes( null, null );
        this.chart.yAxis[0].setExtremes( null, null );
        this.plotsService.setZoom( null, null, this.plotId );
    }

    /**
     * Every time we want to update the chart config object we can run this function
     * Can be configured to redraw the plot or recreate the plot if needed.
     */
    updateConfig( redraw = false ) {
        this.chart.update( cloneDeep( this.config ), redraw );
    }

    /** Everytime menu options change */
    updateMenuOptions( menuOptions: IMenuOptions ) {
        const datasets = this._datasetService.getPlotDatasets( this.plotId ).datasets;
        if ( datasets.length === 0 ) {
            return;
        }
        const xAxisDataset = datasets.find( dataset => dataset.uid === menuOptions.overplot.xAxisDSID )
                              ? datasets.find( dataset => dataset.uid === menuOptions.overplot.xAxisDSID )
                              : datasets[0];
        const limitsDataset = datasets.find( dataset => dataset.uid === menuOptions.overplot.limitsDSID )
                              ? datasets.find( dataset => dataset.uid === menuOptions.overplot.limitsDSID )
                              : datasets[0];

        this.config = processMenuConfig(
            this.config,
            menuOptions,
            this.uiOptions.colorTheme,
            limitsDataset,
            this.chart.chartHeight
        );
        this.handleColorThemeChange();

        if ( menuOptions.yAxis.useMultipleAxes ) {
            datasets.forEach( ( dataset, index ) => {
                if ( index > 0 ) {
                    this.moveDatasetToAxis( index + 1, dataset );
                    this.chart.yAxis[ index + 1 ].update({ labels: { style: { color: dataset.color } } }, false );
                }
            });
            menuOptions.view.limitAreas = false; // disable limit areas
        } else {
            // move datasets to the primary axis
            datasets.forEach( dataset => this.moveDatasetToAxis( 0, dataset ));
        }

        // set limit violation coloring
        this.setColorZones( menuOptions.view.limitViolationColoring );

        this.allowGaps = menuOptions.dataDisplay.allowGaps;
        this.gapThreshold = menuOptions.dataDisplay.thresholdRatio;
        this.setGapOptions();

        if ( this.events ) {
            this.updateViewEventTypes();
        }

        this.chart.yAxis.forEach( ( axis, index ) => {
            if ( index !== 1 ) {
                axis.update({ visible: menuOptions.view.yAxes }, false );
            }
        });

        // update the x axis labels
        const xAxisOffset = xAxisDataset.offset as number;
        const xAxisFormatters = setXAxisLabels( menuOptions.xAxis.labels, xAxisOffset );
        this.config.xAxis[0].labels.formatter = xAxisFormatters.formatter;
        this.config.navigator.xAxis.labels = {
            formatter: xAxisFormatters.navigatorFormatter
        };

        // update visibility of annotations and comments
        this.setAnnotationsVisibility( menuOptions.annotations.show );

        // update once with all changes
        this.chart.update( cloneDeep( this.config ) );
    }

    /** set whether the annotations are visible or not */
    setAnnotationsVisibility( visible: boolean ) {
        this.chart['annotations'].forEach( annotation => annotation.setVisibility( visible ) );
    }

    /** whenever the darkTheme value changes, this function is called and handles the changes accordingly. */
    handleColorThemeChange() {
        if ( this.uiOptions.darkTheme ) {
            // this ensures that overlay containers get our dark theme
            this._overlayContainer.getContainerElement().classList.add('dark-theme');
            this.uiOptions.colorTheme = COLOR_THEMES.dark;
        } else {
            this._overlayContainer.getContainerElement().classList.remove('dark-theme');
            this.uiOptions.colorTheme = COLOR_THEMES.light;
        }

        const datasets = this._datasetService.getPlotDatasets( this.plotId ).datasets;
        this.config = setColorTheme( this.config, this.uiOptions.colorTheme );

        // change the colors of the series
        datasets.forEach( ( dataset, index ) => {
            dataset.color = this.uiOptions.colorTheme.colors[ index ];
            this.chart.series.forEach( ( series, i ) => {
                if ( series.options.id === dataset.uid ) {
                    series.update({ color: dataset.color } as Highstock.SeriesLineOptions, false);
                    // if we have multiple axis, set the color of each axis to the series color it represents
                    if ( this.menuOptions.yAxis.useMultipleAxes ) {
                        this.chart.yAxis[ i ].update({ labels: { style: { color: datasets[0].color } } }, false );
                    }
                }
            });
        });
        // if we only have 1 axis, set the primary axis to the primary axis color
        if ( !this.menuOptions.yAxis.useMultipleAxes ) {
            this.chart.yAxis[ 0 ].update({ labels: { style: { color: this.uiOptions.colorTheme.axis.labelStyle.color } } }, false );
        }
    }

    /** move dataset to a different y axis */
    moveDatasetToAxis( axisIndex: number, dataset: IDataset ) {
        this.chart.series.forEach( series => {
            if ( series.options.id === dataset.uid ) {
                series.update({ yAxis: axisIndex } as Highstock.SeriesLineOptions, false );
            }
        });
    }

    /** sets the color of the series to show limit violations */
    setColorZones( enabled: boolean ) {
        const datasets = this._datasetService.getPlotDatasets( this.plotId ).datasets;
        const zoneColors = COLOR_THEMES.light.limits.zones;
        const chartDataZones = datasets.map( dataset => {
            return enabled ? getLimitZones( dataset.metadata, zoneColors.good, zoneColors.warn, zoneColors.bad ) : [];
        });
        datasets.forEach( ( dataset, index ) => {
            this.chart.series.forEach( series => {
                if ( series.options.id === dataset.uid ) {
                    series.update({ zones: chartDataZones[ index ] } as Highstock.SeriesLineOptions, false );
                }
            });
        });
    }

    /** add each event as a series to the chart */
    addEventsToChart() {
        this.events.forEach( event => {
            this.addEventToChart( event, getColorForEventType( event.type, this.eventTypes, this.uiOptions.colorTheme ).series );
        });
    }

    /** updates the view of events on the chart */
    updateViewEventTypes() {
        // update chart options to show or hide the extra y-axis
        this.config = setEventsOverlay(
            this.config,
            this.menuOptions.view.events,
            this.events,
            this.eventTypes,
            this.menuOptions.view.eventTypes,
            this.uiOptions.colorTheme
        );
        this.setEventTypeVisibility( this.menuOptions.view.eventTypes );
    }

    /** Sets the visibility of specific event types */
    setEventTypeVisibility( eventTypesToView: number[] ) {
        // for each events series, if the event type is in the list of eventTypesToView, show it. Otherwise, hide it.
        this.chart.series.filter( series => series.name === 'Event' ).forEach( series => {
            series.setVisible( eventTypesToView.includes( series.options['eventDetails'].type.id ), false );
        });
    }

    /** add a new series for an event to the chart */
    addEventToChart( event: IEvent, color: string ) {
        const extremes = this.chart.xAxis[0].getExtremes();
        const minTime = extremes.dataMin;
        const maxTime = extremes.dataMax;
        // It's possible that an event will have a start time which is before the start of the plot's loaded time range,
        // or have an end time which is after the end of the loaded time range. This causes a few bugs.
        // Ensure that the polygon series will only be drawn within the loaded range.
        const xStart = typeof minTime === 'undefined' ? event.start : Math.max( minTime, event.start );
        const xEnd = typeof maxTime === 'undefined' ? event.end : Math.min( maxTime, event.end );
        // Each event type is placed at a different integer y-value.
        // Adjust the y-values so that each event rect takes up almost a height of 1
        const eventThickness = 14 / 16;
        const halfMargin = ( 1 - eventThickness ) / 2;
        // start by drawing a vertical line. If the event is discrete, this line will be a few pixels wide
        const data = [ [ xStart, event.y + halfMargin ], [ xStart, event.y + 1 - halfMargin ] ];

        // if the event has a duration, draw a rectangle instead of a line. Add a couple more points to make it a rectangle.
        if ( event.end !== event.start ) {
            data.push( [ xEnd, event.y + 1 - halfMargin ], [ xEnd, event.y + halfMargin ] );
        }

        this.chart.addSeries({
            eventDetails: event, // store event details for the modal that pops up when the event is clicked on
            lineWidth: event.start === event.end ? 3 : 1, // thick line for instantaneous events, rectangle with thin lines otherwise
            color: color,
            type: 'polygon',
            data: data,
            name: 'Event',
            yAxis: 1
        } as Highstock.SeriesPolygonOptions, false );
    }

    /** finds where discrete values have integer gaps */
    findYAxisBreaks() {
        const datasets = this._datasetService.getPlotDatasets( this.plotId ).datasets;
        // we may have duplicate values. Get only unique values.
        this.yAxisBreaks = this.calculateYAxisBreaks( datasets.map( dataset => getUsedDiscreteVals( dataset.data, dataset.indexes ) ) );
    }

    /** chops out unused integers so we get a continous range of discrete values for discrete plots */
    calculateYAxisBreaks( uniqueVals ) {
        // ensure the array is sorted numerically
        uniqueVals.sort( ( a, b ) => a - b );
        // discrete values are always integers. Create breaks that start and end between integers (at n.5).
        // this chops out the unused values while keeping the labels we want to see, and retaining an even
        // spacing on the axis between the used values
        const yAxisBreaks = [];
        if ( uniqueVals.length > 1 ) {
            // the loop below intentionally starts at 1 instead of 0 because we need to refer to i-1
            for ( let i = 1; i < uniqueVals.length; i++ ) {
                // place an axis break between the values defined at i-1 and i
                // don't add an axis break if the values are consecutive integers
                if ( uniqueVals[ i - 1 ] + 1 === uniqueVals[ i ] ) {
                    continue;
                }
                yAxisBreaks.push({
                    from: uniqueVals[ i - 1 ] + 0.5,
                    to: uniqueVals[ i ] - 0.5
                });
            }
        }
        return yAxisBreaks;
    }
}
